import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Spin,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomUpload from "../../../components/Upload";
import { message } from "../../../components/Message";
import errorCode from "../../../utils/errCode";
import { useWorkspace } from "../../../context/WorkspaceRegionProvider";
import {
  fetchIntlRegionCodeList,
  fetchIntlSoftDetailMerged,
  saveIntlSoftDetail,
} from "../hooks/useIntlSoftApi";
import {
  buildIntlSavePayload,
  checkImageUploadStatus,
  deriveLanguageStateFromDetail,
  getIntlOsBitOptions,
  getIntlRunEnvOptions,
  isDefaultLanguageTabComplete,
  isExtendedLanguageTabComplete,
  mapDetailToRegisterForm,
  normalizeIntlOsBitValues,
  normalizeIntlRunEnvValues,
  pickDetailMeta,
  resolveIntlAppCateIdFromSources,
  resolveIntlDefaultLanguageCode,
  validateInstallPackage,
  isValidIntlFeeValue,
} from "../../../utils/intlRegister";
import {
  flattenCountryCodes,
  inferRegionStateFromDetail,
  validateRegionSelection,
} from "../../../utils/intlRegion";
import InstallPackageSection from "./components/InstallPackageSection";
import LanguageSection from "./components/LanguageSection";
import RegionSection from "./components/RegionSection";
import cnRegisterStyles from "../../register/index.modules.less";
import styles from "./index.modules.less";

const tailFormItemLayout = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 16, offset: 8 },
  },
};

const IntlRegister = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { config, oss } = useSelector((state) => state);
  const { canUseIntl } = useWorkspace();

  const [detailLoading, setDetailLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailMeta, setDetailMeta] = useState({});
  const [detailRaw, setDetailRaw] = useState({});

  const [languageOptions, setLanguageOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [portal11Status, setPortal11Status] = useState("idle");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [activeTab, setActiveTab] = useState("");
  /** 保存校验未通过后，Tab「待填」标红 */
  const [pendingTabsHighlight, setPendingTabsHighlight] = useState(false);

  const [regionMode, setRegionMode] = useState("global");
  const [selectedCountryCodes, setSelectedCountryCodes] = useState([]);

  const allCountryCodes = useMemo(
    () => flattenCountryCodes(regionOptions),
    [regionOptions]
  );

  const intlRunEnvOptions = useMemo(
    () => getIntlRunEnvOptions(config?.system_env),
    [config?.system_env]
  );

  const intlOsBitOptions = useMemo(() => getIntlOsBitOptions(), []);

  const loadPortal11Options = useCallback(async () => {
    setPortal11Status("loading");
    try {
      const res = await fetchIntlRegionCodeList();
      const languages = Array.isArray(res?.language_code) ? res.language_code : [];
      const regions = Array.isArray(res?.region) ? res.region : [];
      setLanguageOptions(languages);
      setRegionOptions(regions);
      setPortal11Status("ready");
      return { languages, regions };
    } catch (error) {
      setLanguageOptions([]);
      setRegionOptions([]);
      setPortal11Status("failed");
      message.show({
        type: "error",
        content: errorCode[error?.errno] || error?.errmsg || "地区/语言枚举加载失败",
      });
      return { languages: [], regions: [] };
    }
  }, []);

  const applyDetailToForm = useCallback(
    (detail, options, regions) => {
      const languageState = deriveLanguageStateFromDetail(detail, options);
      const regionState = inferRegionStateFromDetail(detail, regions);

      form.setFieldsValue(mapDetailToRegisterForm(detail, options));
      form.setFieldsValue({
        run_envs: normalizeIntlRunEnvValues(
          form.getFieldValue("run_envs"),
          config?.system_env
        ),
        os_osbit: (() => {
          const normalized = normalizeIntlOsBitValues(form.getFieldValue("os_osbit"));
          return normalized.length ? normalized : ["64"];
        })(),
      });
      setRegionMode(regionState.mode);
      setSelectedCountryCodes(regionState.selectedCountryCodes);

      if (languageState.selectedLanguageCodes.length) {
        setSelectedLanguages(languageState.selectedLanguageCodes);
        setDefaultLanguage(languageState.defaultLanguageCode);
        setActiveTab(languageState.defaultLanguageCode);
      } else {
        const defaultCode = resolveIntlDefaultLanguageCode(options);
        if (defaultCode) {
          setSelectedLanguages([defaultCode]);
          setDefaultLanguage(defaultCode);
          setActiveTab(defaultCode);
        }
      }

      if (detail.app_pkg_key) {
        dispatch.oss.setOssObj({ app_pkg_key: detail.app_pkg_key });
      }
    },
    [dispatch.oss, form, config?.system_env]
  );

  const initPage = useCallback(async () => {
    if (!id || !canUseIntl) {
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    try {
      const { languages, regions } = await loadPortal11Options();
      const detail = await fetchIntlSoftDetailMerged(id);
      const appCateId = resolveIntlAppCateIdFromSources(
        location.state,
        detail
      );
      const detailWithCate = appCateId
        ? { ...detail, app_cate_id: appCateId, app_cate_d: appCateId }
        : detail || {};
      setDetailRaw(detailWithCate);
      setDetailMeta(pickDetailMeta(detailWithCate));
      applyDetailToForm(detailWithCate, languages || [], regions || []);
    } catch (error) {
      message.show({
        type: "error",
        content: errorCode[error?.errno] || error?.errmsg || "上架配置加载失败",
      });
    } finally {
      setDetailLoading(false);
    }
  }, [id, canUseIntl, loadPortal11Options, applyDetailToForm, location.state]);

  useEffect(() => {
    dispatch.config.setConfigAsync();
  }, [dispatch.config]);

  useEffect(() => {
    if (!detailRaw?.id || !config?.system_env?.length) {
      return;
    }
    form.setFieldsValue({
      run_envs: normalizeIntlRunEnvValues(
        form.getFieldValue("run_envs"),
        config.system_env
      ),
      os_osbit: (() => {
        const normalized = normalizeIntlOsBitValues(form.getFieldValue("os_osbit"));
        return normalized.length ? normalized : ["64"];
      })(),
    });
  }, [config?.system_env, detailRaw?.id, form]);

  useEffect(() => {
    if (!id) {
      message.show({ type: "error", content: "缺少软件 ID，请从列表进入上架配置" });
      navigate("/view/home", { replace: true });
      return;
    }
    initPage();
  }, [id, initPage, navigate]);

  const getDefaultLanguageFieldNames = () => [
    "app_name",
    "logo_pic",
    "running_pic",
    "feature",
  ];

  const getExtendedLanguageFieldNames = (languageCode) => [
    ["extendedLang", languageCode, "soft_name"],
    ["extendedLang", languageCode, "soft_logo"],
    ["extendedLang", languageCode, "soft_snapshot"],
    ["extendedLang", languageCode, "soft_description"],
  ];

  /** 触发指定语言 Tab 字段的 Form.Item 红字提示（不抛到 onSave） */
  const triggerLanguageFieldErrors = async (languageCode, isDefault) => {
    const names = isDefault
      ? getDefaultLanguageFieldNames()
      : getExtendedLanguageFieldNames(languageCode);
    try {
      await form.validateFields(names);
    } catch {
      // 预期：缺填字段会挂上 error，供 Form.Item 展示红字
    }
  };

  /** 给所有未填全的语言挂上红字；切到第一个未填全的 Tab */
  const markIncompleteLanguageErrors = async (draftValues) => {
    const incompleteCodes = [];

    if (!isDefaultLanguageTabComplete(draftValues, defaultLanguage)) {
      incompleteCodes.push({ code: defaultLanguage, isDefault: true });
    }

    selectedLanguages
      .filter((code) => code !== defaultLanguage)
      .forEach((code) => {
        if (!isExtendedLanguageTabComplete(draftValues, code)) {
          incompleteCodes.push({ code, isDefault: false });
        }
      });

    if (!incompleteCodes.length) {
      setPendingTabsHighlight(false);
      return null;
    }

    setPendingTabsHighlight(true);

    // 先全部校验挂上 error（含当前隐藏 Tab），再切到第一个未填 Tab
    await Promise.all(
      incompleteCodes.map(({ code, isDefault }) =>
        triggerLanguageFieldErrors(code, isDefault)
      )
    );

    const first = incompleteCodes[0];
    setActiveTab(first.code);
    // 再等一帧，确保切到可见 Tab 后红字能渲染出来
    await new Promise((resolve) => {
      window.setTimeout(resolve, 0);
    });
    await triggerLanguageFieldErrors(first.code, first.isDefault);

    return first.isDefault ? "default" : "extended";
  };

  const validateBeforeSave = async () => {
    if (portal11Status === "failed") {
      message.show({ type: "error", content: "地区/语言枚举不可用，请重试后再保存" });
      return false;
    }

    if (!selectedLanguages.length) {
      message.show({ type: "error", content: "请至少选择一种生效语言" });
      return false;
    }

    const regionError = validateRegionSelection({
      mode: regionMode,
      selectedCountryCodes,
      allCountryCodes,
    });
    if (regionError) {
      message.show({ type: "error", content: regionError });
      return false;
    }

    // 先用全量表单值做多语言完整性检查（含隐藏 Tab），避免 validateFields
    // 因隐藏必填项抛错后被 onSave 统一成「保存失败」
    const draftValues = form.getFieldsValue(true);
    const incompleteKind = await markIncompleteLanguageErrors(draftValues);
    if (incompleteKind === "default") {
      message.show({ type: "error", content: "请完善默认语言的名称、Logo、截图与介绍" });
      return false;
    }
    if (incompleteKind === "extended") {
      message.show({ type: "error", content: "请完善各扩展语言的必填素材" });
      return false;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      // Ant Design 字段校验失败：切到对应语言 Tab，不走「保存失败」
      const namePath = error?.errorFields?.[0]?.name;
      if (Array.isArray(namePath) && namePath[0] === "extendedLang" && namePath[1]) {
        const code = String(namePath[1]);
        setActiveTab(code);
        message.show({ type: "error", content: "请完善各扩展语言的必填素材" });
      } else if (
        namePath === "app_name" ||
        namePath === "logo_pic" ||
        namePath === "running_pic" ||
        namePath === "feature" ||
        (Array.isArray(namePath) &&
          ["app_name", "logo_pic", "running_pic", "feature"].includes(namePath[0]))
      ) {
        setActiveTab(defaultLanguage);
        message.show({ type: "error", content: "请完善默认语言的名称、Logo、截图与介绍" });
      } else {
        const tip = error?.errorFields?.[0]?.errors?.[0];
        message.show({
          type: "error",
          content: tip || "请完善必填信息后再保存",
        });
      }
      return false;
    }

    if (!checkImageUploadStatus(values.certificate_pic)) {
      message.show({ type: "error", content: "请重新上传资质图" });
      return false;
    }

    const installError = validateInstallPackage(values, values.upload_type);
    if (installError) {
      message.show({ type: "error", content: installError });
      return false;
    }

    return true;
  };

  const onSave = async () => {
    try {
      const valid = await validateBeforeSave();
      if (!valid) {
        return;
      }

      const formValues = form.getFieldsValue(true);
      const payload = buildIntlSavePayload({
        formValues,
        defaultLanguage,
        selectedLanguages,
        regionState: {
          mode: regionMode,
          selectedCountryCodes,
          allCountryCodes,
        },
        saveContext: {
          detailMeta,
          detailRaw,
          oss,
        },
      });

      if (!payload.white_list_region?.length) {
        message.show({ type: "error", content: "生效地区不能为空" });
        return;
      }

      setSaving(true);
      await saveIntlSoftDetail(payload);
      message.show({ type: "success", content: "保存成功" });
      navigate("/view/home");
    } catch (error) {
      message.show({
        type: "error",
        content: errorCode[error?.errno] || error?.errmsg || "保存失败",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!canUseIntl) {
    return (
      <div className={cnRegisterStyles.registerWrapper}>
        <div className={cnRegisterStyles.back} onClick={() => navigate(-1)}>
          {"<返回"}
        </div>
        <div className={styles.placeholder}>当前企业未开通国际分发能力</div>
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className={cnRegisterStyles.registerWrapper}>
        <Spin size="large" style={{ display: "block", margin: "80px auto" }} />
      </div>
    );
  }

  return (
    <div className={cnRegisterStyles.registerWrapper}>
      <div onClick={() => navigate(-1)} className={cnRegisterStyles.back}>
        {"<返回"}
      </div>

      <Form
        form={form}
        name="intl_register"
        scrollToFirstError
        labelCol={{ style: { width: "150px" } }}
      >
        <section className={styles.section}>
          <div className={styles.sectionTitle}>基础信息与分发策略</div>
          <Row className="first-row">
            <Col>
              <Form.Item
                name="app_version"
                label="软件版本"
                rules={[{ required: true, message: "请输入软件版本" }]}
              >
                <Input placeholder="例如：1.0.0.1" className="appinfo-input" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="app_cate_id"
            label="软件类型"
            rules={[{ required: true, message: "请选择软件类型！" }]}
          >
            <Select className="appinfo-app_cate" placeholder="请选择软件类型！">
              {(config?.soft_cate || []).map((item) => (
                <Select.Option key={item.id} value={Number(item.id)}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <InstallPackageSection form={form} softForm={detailRaw} />

          <Form.Item
            name="certificate_pic"
            label="上传资质"
            htmlFor={undefined}
            valuePropName="fileList"
            getValueFromEvent={(event) => event}
            rules={[{ required: true, message: "请上传资质图" }]}
          >
            <CustomUpload
              tips="请务必提交软件著作权证书，如有其他资质请一并提交；图片不超过 5M，格式 jpg/jpeg/png"
              maxCount={5}
              name="certificate_pic"
              form={form}
              accept=".jpg, .jpeg, .png"
              size={5}
            />
          </Form.Item>

          <Form.Item
            name="run_envs"
            label="系统版本要求"
            htmlFor={undefined}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const validValues = normalizeIntlRunEnvValues(
                    value,
                    config?.system_env
                  );
                  if (!validValues.length) {
                    return Promise.reject(new Error("请选择 Win10 或 Win11"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Checkbox.Group>
              {intlRunEnvOptions.map((item) => (
                <Checkbox key={item.id} value={`${item.id}`} style={{ lineHeight: "32px" }}>
                  {item.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="os_osbit"
            label="系统位数要求"
            htmlFor={undefined}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const validValues = normalizeIntlOsBitValues(value);
                  if (!validValues.length) {
                    return Promise.reject(new Error("请选择 64 位"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Checkbox.Group>
              {intlOsBitOptions.map((item) => (
                <Checkbox key={item.value} value={item.value} style={{ lineHeight: "32px" }}>
                  {item.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="fee"
            label="付费信息"
            htmlFor={undefined}
            className="appinfo-free-item"
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!isValidIntlFeeValue(value)) {
                    return Promise.reject(new Error("请选择付费方式!"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Radio.Group>
              <Radio value={2}>免费</Radio>
              <Radio value={1}>收费</Radio>
              <Radio value={3}>试用</Radio>
              <Form.Item dependencies={["fee"]} style={{ display: "inline-block", marginBottom: 0 }}>
                {({ getFieldValue }) => (
                  <Form.Item name="free_trial_days" style={{ marginBottom: 0 }}>
                    <InputNumber
                      className="appinfo-free_trial_days"
                      disabled={getFieldValue("fee") !== 3}
                      min={0}
                    />
                  </Form.Item>
                )}
              </Form.Item>
              <Radio value={4}>部分功能收费</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="new_feature" label="更新日志">
            <Input.TextArea
              className="appinfo-newfeature"
              showCount
              placeholder="根级更新日志（全语言共享，不在多语言 Tab 内重复）"
              maxLength={300}
            />
          </Form.Item>

          <Form.Item name="install_params" label="静默安装参数">
            <Input.TextArea
              className="appinfo-params"
              placeholder="静默安装参数，例如 /VERYSILENT"
              maxLength={100}
            />
          </Form.Item>

          <div className={styles.sectionDivider} />

          <LanguageSection
            form={form}
            languageOptions={languageOptions}
            portal11Status={portal11Status}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
            defaultLanguage={defaultLanguage}
            setDefaultLanguage={setDefaultLanguage}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRetryLoadOptions={loadPortal11Options}
            showLanguageTabs={false}
            detailRaw={detailRaw}
            pendingTabsHighlight={pendingTabsHighlight}
          />

          <RegionSection
            regionOptions={regionOptions}
            portal11Status={portal11Status}
            regionMode={regionMode}
            setRegionMode={setRegionMode}
            selectedCountryCodes={selectedCountryCodes}
            setSelectedCountryCodes={setSelectedCountryCodes}
            onRetryLoadOptions={loadPortal11Options}
          />
        </section>

        <section className={`${styles.section} ${styles.multiLangSection}`}>
          <LanguageSection
            form={form}
            languageOptions={languageOptions}
            portal11Status={portal11Status}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
            defaultLanguage={defaultLanguage}
            setDefaultLanguage={setDefaultLanguage}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onRetryLoadOptions={loadPortal11Options}
            showLanguagePicker={false}
            detailRaw={detailRaw}
            pendingTabsHighlight={pendingTabsHighlight}
          />
        </section>

        <Form.Item {...tailFormItemLayout}>
          <div className={styles.footerBar}>
            <Button className="btn-cancel" onClick={() => navigate(-1)}>
              取消
            </Button>
            <Button
              className="btn-add"
              type="primary"
              onClick={onSave}
              loading={saving}
              disabled={portal11Status === "failed"}
            >
              保存并提交审核
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default IntlRegister;
